import { Plus, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  useApproveVariation,
  useCreateVariation,
  useDeclineVariation,
  useExecuteVariation,
  useVariations,
  useWithdrawVariation,
} from '@/hooks/use-variations';
import { cn } from '@/lib/cn';
import { fmtDate as formatDate } from '@/lib/format';
import { toast } from '@/stores/toast-store';

interface VariationTabProps {
  loanId: string;
  loan: any;
}

const VARIATION_TYPES = [
  { value: 'RATE_CHANGE', label: 'Rate Change' },
  { value: 'TERM_EXTENSION', label: 'Term Extension' },
  { value: 'PRINCIPAL_INCREASE', label: 'Principal Increase' },
  { value: 'PRINCIPAL_REDUCTION', label: 'Principal Reduction' },
  { value: 'PAYMENT_ADJUSTMENT', label: 'Payment Adjustment' },
];

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700' },
  PENDING_APPROVAL: { bg: 'bg-amber-100', text: 'text-amber-700' },
  APPROVED: { bg: 'bg-blue-100', text: 'text-blue-700' },
  EXECUTED: { bg: 'bg-green-100', text: 'text-green-700' },
  DECLINED: { bg: 'bg-red-100', text: 'text-red-700' },
  WITHDRAWN: { bg: 'bg-gray-100', text: 'text-gray-500 line-through' },
};

function formatVariationValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toLocaleString('en-AU') : '—';
  }
  if (typeof value === 'string') {
    return value.trim() || '—';
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if ('value' in record) {
      return formatVariationValue(record.value);
    }
    if ('amount' in record) {
      return formatVariationValue(record.amount);
    }
    if ('from' in record || 'to' in record) {
      const from = formatVariationValue(record.from);
      const to = formatVariationValue(record.to);
      return `${from} → ${to}`;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '—';
    }
  }
  return String(value);
}

export function VariationTab({ loanId, loan: _loan }: VariationTabProps) {
  const { data: variations, isLoading } = useVariations(loanId);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    oldValue?: string;
    newValue?: string;
  }>({});
  const [formData, setFormData] = useState({
    type: '',
    description: '',
    effectiveDate: '',
    oldValue: '',
    newValue: '',
  });

  const createVariation = useCreateVariation();
  const approveVariation = useApproveVariation();
  const declineVariation = useDeclineVariation();
  const executeVariation = useExecuteVariation();
  const withdrawVariation = useWithdrawVariation();

  const sanitizeDecimalInput = (value: string) => value.replace(/[^0-9.]/g, '');

  const validateNonNegative = (value: string) => {
    if (!value.trim()) return '';
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 'Enter a valid number.';
    if (parsed < 0) return 'Value cannot be negative.';
    return '';
  };

  const handleCreateVariation = async () => {
    if (!formData.type || !formData.effectiveDate) {
      toast.error('Please fill in required fields');
      return;
    }

    const nextErrors = {
      oldValue: validateNonNegative(formData.oldValue),
      newValue: validateNonNegative(formData.newValue),
    };
    setFormErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      toast.error('Please correct the highlighted fields');
      return;
    }

    createVariation.mutate(
      {
        loanId,
        type: formData.type,
        description: formData.description,
        effectiveDate: formData.effectiveDate,
        oldValue: formData.oldValue,
        newValue: formData.newValue,
      },
      {
        onSuccess: () => {
          toast.success('Variation created');
          setFormData({ type: '', description: '', effectiveDate: '', oldValue: '', newValue: '' });
          setFormErrors({});
          setShowCreateForm(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading variations…
      </div>
    );
  }

  const variationList = Array.isArray(variations)
    ? variations
    : Array.isArray((variations as any)?.variations)
      ? (variations as any).variations
      : Array.isArray((variations as any)?.data)
        ? (variations as any).data
        : [];

  return (
    <div className="space-y-4">
      {/* Create button */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-900">Variations</h3>
        <Button
          variant="primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Variation
        </Button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select type</option>
                {VARIATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
              <input
                type="date"
                value={formData.effectiveDate}
                onChange={e => setFormData({ ...formData, effectiveDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Old Value</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.oldValue}
                onChange={e => {
                  setFormData({ ...formData, oldValue: sanitizeDecimalInput(e.target.value) });
                  setFormErrors(prev => ({ ...prev, oldValue: undefined }));
                }}
                onBlur={() =>
                  setFormErrors(prev => ({
                    ...prev,
                    oldValue: validateNonNegative(formData.oldValue),
                  }))
                }
                className={cn(
                  'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  formErrors.oldValue ? 'border-red-300 bg-red-50' : 'border-gray-200',
                )}
                placeholder="Current value"
              />
              {formErrors.oldValue && (
                <p className="mt-1 text-xs text-red-600">{formErrors.oldValue}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Value</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.newValue}
                onChange={e => {
                  setFormData({ ...formData, newValue: sanitizeDecimalInput(e.target.value) });
                  setFormErrors(prev => ({ ...prev, newValue: undefined }));
                }}
                onBlur={() =>
                  setFormErrors(prev => ({
                    ...prev,
                    newValue: validateNonNegative(formData.newValue),
                  }))
                }
                className={cn(
                  'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                  formErrors.newValue ? 'border-red-300 bg-red-50' : 'border-gray-200',
                )}
                placeholder="New value"
              />
              {formErrors.newValue && (
                <p className="mt-1 text-xs text-red-600">{formErrors.newValue}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Description of changes…"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-3 py-1.5 border border-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <Button
              variant="primary"
              onClick={handleCreateVariation}
              disabled={createVariation.isPending}
              className="px-3 py-1.5 text-white rounded-md text-sm font-medium hover: transition-colors disabled:opacity-50"
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {/* Variations list */}
      {variationList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 bg-white border border-gray-200 rounded-lg">
          <TrendingUp className="w-8 h-8 mb-2" />
          <p className="text-sm">No variations yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {variationList.map((variation: any) => {
            const statusColor = STATUS_COLORS[variation.status] || STATUS_COLORS.DRAFT;
            const typeLabel =
              VARIATION_TYPES.find(t => t.value === variation.type)?.label || variation.type;

            return (
              <div
                key={variation.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
              >
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{typeLabel}</p>
                      <p className="text-xs text-gray-500">
                        {variation.description || 'No description provided'}
                      </p>
                    </div>
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium px-2 py-1 rounded-full',
                      statusColor.bg,
                      statusColor.text,
                    )}
                  >
                    {variation.status.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Content */}
                <div className="px-4 py-3 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Old Value</p>
                      <p className="font-medium text-gray-900">
                        {formatVariationValue(variation.previousValues)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">New Value</p>
                      <p className="font-medium text-indigo-600">
                        {formatVariationValue(variation.newValues)}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                    <span>Effective: {formatDate(variation.effectiveDate)}</span>
                    <span>
                      Created: {formatDate(variation.createdAt)}
                      {variation.createdBy &&
                        ` by ${variation.createdBy.firstName} ${variation.createdBy.lastName}`}
                    </span>
                    {variation.executedAt && (
                      <span>Executed: {formatDate(variation.executedAt)}</span>
                    )}
                    {variation.approvedAt && (
                      <span>
                        Approved: {formatDate(variation.approvedAt)}
                        {variation.approvedBy &&
                          ` by ${variation.approvedBy.firstName} ${variation.approvedBy.lastName}`}
                      </span>
                    )}
                    {variation.declinedReason && (
                      <span>Decline reason: {variation.declinedReason}</span>
                    )}
                  </div>

                  {/* Actions */}
                  {variation.status === 'PENDING_APPROVAL' && (
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() =>
                          approveVariation.mutate(
                            { loanId, id: variation.id },
                            {
                              onSuccess: () => toast.success('Variation approved'),
                            },
                          )
                        }
                        disabled={approveVariation.isPending}
                        className="flex-1 px-3 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() =>
                          declineVariation.mutate(
                            { loanId, id: variation.id, reason: 'Declined by user' },
                            {
                              onSuccess: () => toast.success('Variation declined'),
                            },
                          )
                        }
                        disabled={declineVariation.isPending}
                        className="flex-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        Decline
                      </button>
                    </div>
                  )}

                  {variation.status === 'APPROVED' && (
                    <button
                      onClick={() =>
                        executeVariation.mutate(
                          { loanId, id: variation.id },
                          {
                            onSuccess: () => toast.success('Variation executed'),
                          },
                        )
                      }
                      disabled={executeVariation.isPending}
                      className="w-full px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      Execute
                    </button>
                  )}

                  {(variation.status === 'DRAFT' || variation.status === 'PENDING_APPROVAL') && (
                    <button
                      onClick={() =>
                        withdrawVariation.mutate(
                          { loanId, id: variation.id },
                          {
                            onSuccess: () => toast.success('Variation withdrawn'),
                          },
                        )
                      }
                      disabled={withdrawVariation.isPending}
                      className="w-full px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
